import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  discrepancyFieldKey,
  discrepancySourceKey,
  type Discrepancy,
  type FinalCheck,
} from '@entities/conclusion';
import { money } from '@shared/lib/money';
import { Badge, Button, Checkbox } from '@shared/ui';

import styles from './FinalCheckBlock.module.css';

interface FinalCheckBlockProps {
  check: FinalCheck;
  /** Идёт запрос подтверждения: кнопка блокируется, состояние не рисуется вперёд. */
  pending?: boolean | undefined;
  /** `null` — подтверждать нечего: экран только показывает расхождения. */
  onAcknowledge?: (() => void) | undefined;
}

/**
 * Финальная сверка перед решением (FE-250, MVP_PRD §38.3).
 *
 * Пять правил блока, и каждое — из документа:
 *
 * * **обе стороны в равных по весу колонках.** CarViQ.DE не говорит, какая из них
 *   верная: он показывает, что объявление, мандат или ответ автохауса
 *   расходятся с финальным предложением, и приводит **обе цитаты**;
 * * **формулировки нейтральные.** Ни «обман», ни «недостоверно», ни «продавец
 *   скрыл»: функция возвращает факт различия, а не вывод о причине (§34.9), и
 *   интерфейс обязан остаться на том же уровне;
 * * **блок стоит над кнопками решения**, а не под ними и не в раскрывающемся
 *   разделе: расхождение нельзя пропустить, прокрутив страницу;
 * * **существенное расхождение приостанавливает согласие.** Пока пользователь
 *   не подтвердил, что видел его, «Согласиться» неактивна. Настоящая проверка
 *   живёт в сервисном слое — неактивная кнопка только подсказка (§38.3);
 * * **подтверждение не означает согласия с расхождением.** Оно означает ровно
 *   «я это видел», и подпись говорит именно это.
 *
 * Пустой список расхождений **показывается** отдельной строкой: «сверка прошла,
 * различий не нашла» — это результат работы, а не отсутствие блока. Он при этом
 * не утверждает, что с автомобилем всё в порядке: сверяются поля предложения, а
 * не автомобиль.
 *
 * Редизайн 2026-10 (DESIGN_SYSTEM §8): расхождение — **оранжевый «требует
 * внимания» с иконкой**, без красного, без «Fehler/falsch». «Я это видел» —
 * действие на карточке, синий контур (`outline-action`, §5.1): красная кнопка
 * экрана одна, и на карточке переписки это «Согласиться».
 */
export function FinalCheckBlock({ check, pending = false, onAcknowledge }: FinalCheckBlockProps) {
  const { t } = useTranslation();
  const acknowledged = check.acknowledged_at !== null;

  return (
    <section className={styles.wrap} aria-label={t('conclusion.finalCheck.title')}>
      <h2 className={styles.title}>{t('conclusion.finalCheck.title')}</h2>
      <p className={styles.note}>{t('conclusion.finalCheck.note')}</p>

      {check.discrepancies.length === 0 ? (
        <p className={styles.clean}>
          {/* Галочка «расхождений нет» — о полях предложения, не об автомобиле. */}
          <CheckCircleIcon size={16} aria-hidden="true" className={styles.cleanIcon} />
          <span>{t('conclusion.finalCheck.clean')}</span>
        </p>
      ) : (
        <ul className={styles.list}>
          {check.discrepancies.map((item) => (
            <li key={`${item.field_code}:${item.left_source}:${item.right_source}`}>
              <DiscrepancyRow item={item} />
            </li>
          ))}
        </ul>
      )}

      {check.blocks_acceptance && onAcknowledge !== undefined ? (
        <div className={styles.acknowledge}>
          <p className={styles.blocked} role="note">
            <WarningCircleIcon size={16} aria-hidden="true" className={styles.blockedIcon} />
            <span>{t('conclusion.finalCheck.blocked', { count: check.material_count })}</span>
          </p>
          <Button variant="outline-action" disabled={pending} onClick={onAcknowledge}>
            {pending
              ? t('conclusion.finalCheck.acknowledgePending')
              : t('conclusion.finalCheck.acknowledge')}
          </Button>
          <p className={styles.meaning}>{t('conclusion.finalCheck.acknowledgeMeaning')}</p>
        </div>
      ) : null}

      {/* Подтверждение остаётся видимым: расхождение никуда не делось, снята
          только блокировка решения (§38.3). */}
      {acknowledged ? (
        <Checkbox checked disabled readOnly>
          {t('conclusion.finalCheck.acknowledged')}
        </Checkbox>
      ) : null}

      <p className={styles.version}>
        {t('conclusion.finalCheck.version', { version: check.algorithm_version })}
      </p>
    </section>
  );
}

/**
 * Одно расхождение: поле, две стороны, две цитаты, метка существенности.
 *
 * Колонки одинаковой ширины и одинакового веса шрифта — это не оформление, а
 * содержание: подсветить одну из сторон значило бы сказать, какая из них
 * правдива, а этого CarViQ.DE не знает.
 */
function DiscrepancyRow({ item }: { item: Discrepancy }) {
  const { t } = useTranslation();

  return (
    <article className={styles.item}>
      <header className={styles.itemHead}>
        <span className={styles.field}>{t(discrepancyFieldKey(item.field_code))}</span>
        {/* Существенное — «требует внимания» с иконкой; второстепенное —
            нейтральная метка (DESIGN_SYSTEM §5.8). */}
        {item.severity === 'material' ? (
          <Badge tone="attention" icon={<WarningCircleIcon weight="bold" />} uppercase={false}>
            {t(`conclusion.discrepancy.severity.${item.severity}`)}
          </Badge>
        ) : (
          <Badge tone="neutral" uppercase={false}>
            {t(`conclusion.discrepancy.severity.${item.severity}`)}
          </Badge>
        )}
      </header>

      <div className={styles.sides}>
        <Side
          source={item.left_source}
          value={item.left_value}
          quote={item.left_quote}
          fieldCode={item.field_code}
        />
        <Side
          source={item.right_source}
          value={item.right_value}
          quote={item.right_quote}
          fieldCode={item.field_code}
        />
      </div>
    </article>
  );
}

function Side({
  source,
  value,
  quote,
  fieldCode,
}: {
  source: string;
  value: string | null;
  quote: string | null;
  fieldCode: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  return (
    <div className={styles.side}>
      <span className={styles.source}>{t(discrepancySourceKey(source))}</span>
      <span className={styles.value}>
        {value === null
          ? t('conclusion.finalCheck.notMentioned')
          : formatValue(value, fieldCode, locale)}
      </span>
      {/* Цитата — способ существования этих данных, а не украшение: со слов
          автохауса, CarViQ.DE не проверял (§2). */}
      {quote === null ? null : <blockquote className={styles.quote}>{quote}</blockquote>}
    </div>
  );
}

/** Поля-суммы приходят в центах: показывать «2100000» пользователю нельзя. */
const CENTS_FIELDS = new Set(['PRICE']);

/**
 * Локаль — языка интерфейса, а не браузера (FE-579): `Intl.NumberFormat(
 * undefined, …)` давал немецкому интерфейсу в английском браузере
 * `€18,224`, да ещё и округлял до евро сумму, которую здесь сверяют.
 */
function formatValue(value: string, fieldCode: string, locale: string): string {
  if (!CENTS_FIELDS.has(fieldCode)) return value;
  const cents = Number.parseInt(value, 10);
  if (Number.isNaN(cents)) return value;
  return money(cents, 'EUR', locale, 'auto');
}
